/**
 * Construcción de registros (puro): qué campos se escriben al crear o editar un hábito,
 * registrar actividad, etc. La web los escribe en IndexedDB y el servidor en su base de
 * datos, con las mismas reglas. Nada aquí lee ni escribe almacenamiento.
 */
import { ueKeyForDate, type HabitVersion, type LocalDate, type Schedule } from '@habitos/engine';
import type { EntityName } from '@habitos/protocol';
import type { AppData, Ctx } from './data';
import { toEngineSettings, toHabitDef, type EntryRec, type HabitRec, type OverrideRec } from './model';

/** Una escritura de campos sobre un registro (crear o modificar). */
export interface RecordWrite {
  entity: EntityName;
  id: string;
  patch: Record<string, unknown>;
}

/**
 * Huella de una escritura, para poder revertirla (deshacer). Solo guarda los campos
 * modificados; de un registro creado no copia nada (basta con borrarlo), así los textos del
 * diario o de las notas no acaban en la conversación.
 */
export interface WriteTrace {
  entity: EntityName;
  id: string;
  created: boolean;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}

/** Huellas de unas escrituras a partir de los datos actuales (antes de escribir). */
export function tracesFor(writes: RecordWrite[], data: AppData): WriteTrace[] {
  const tables: Partial<Record<EntityName, { id: string }[]>> = {
    habits: data.habits,
    entries: data.entries,
    overrides: data.overrides,
    groups: data.groups,
    vacations: data.vacations,
    reminders: data.reminders,
    notes: data.notes,
    listItems: data.listItems,
    journal: data.journal,
    focus: data.focus,
    moves: data.moves,
  };
  return writes.map((w) => {
    const existing = tables[w.entity]?.find((r) => r.id === w.id) as Record<string, unknown> | undefined;
    if (!existing) return { entity: w.entity, id: w.id, created: true, before: {}, after: {} };
    const keys = Object.keys(w.patch);
    return {
      entity: w.entity,
      id: w.id,
      created: false,
      before: Object.fromEntries(keys.map((k) => [k, existing[k] ?? null])),
      after: Object.fromEntries(keys.map((k) => [k, w.patch[k] ?? null])),
    };
  });
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Qué escribir para revertir unas huellas (deshacer), leyendo el estado actual con `current`.
 * - Un registro creado se borra (borrado lógico, como el resto de la app).
 * - Un registro modificado recupera los valores anteriores de los campos tocados, pero solo si
 *   siguen como se dejaron: si alguien los cambió después, no se pisan (conflicto).
 */
export function revertPlan(traces: WriteTrace[], current: (entity: EntityName, id: string) => Record<string, unknown> | undefined, nowIso: string): { writes: RecordWrite[]; conflicts: number } {
  const writes: RecordWrite[] = [];
  let conflicts = 0;
  for (const w of [...traces].reverse()) {
    const row = current(w.entity, w.id);
    if (w.created) {
      if (row && !row.deletedAt) writes.push({ entity: w.entity, id: w.id, patch: { deletedAt: nowIso } });
      continue;
    }
    if (!row || Object.entries(w.after).some(([k, v]) => !same(row[k], v))) {
      conflicts++;
      continue;
    }
    writes.push({ entity: w.entity, id: w.id, patch: w.before });
  }
  return { writes, conflicts };
}

export class PlanLimitError extends Error {
  constructor(readonly limit: number) {
    super(`plan_limit:${limit}`);
  }
}

export class DomainError extends Error {
  constructor(
    readonly code:
      | 'future_date'
      | 'before_start'
      | 'not_scheduled'
      | 'not_found'
      // Motivos de rechazo de un traslado (FR-SCH-12), que vienen de `checkMove`.
      | 'same_day'
      | 'not_movable'
      | 'source_not_scheduled'
      | 'target_out_of_range'
      | 'target_scheduled',
  ) {
    super(code);
  }
}

export interface HabitDraft {
  kind: 'build' | 'quit' | 'task';
  name: string;
  description: string | null;
  icon: string;
  color: string;
  startDate: LocalDate;
  endDate: LocalDate | null;
  dueDate: LocalDate | null;
  /** Hora opcional de una tarea (HH:MM). */
  dueTime: string | null;
  /** Duración prevista en minutos (opcional): ocupa ese tiempo en la agenda. */
  durationMinutes: number | null;
  goalType: 'binary' | 'numeric';
  target: number;
  unit: string | null;
  schedule: Schedule;
  /** Media móvil de N periodos (FR-HAB-16); null = cada día se evalúa por sí solo. */
  average: number | null;
  defaultIncrement: number;
  primaryGroupId: string | null;
  secondaryGroupIds: string[];
  hideStreak: boolean;
  excludedFromProgress: boolean;
}

export function emptyDraft(today: LocalDate, kind: HabitDraft['kind'] = 'build'): HabitDraft {
  return {
    kind,
    name: '',
    description: null,
    icon: kind === 'quit' ? 'cigarette-off' : kind === 'task' ? 'check' : 'sparkles',
    color: kind === 'quit' ? 'coral' : kind === 'task' ? 'blue' : 'violet',
    startDate: today,
    endDate: null,
    dueDate: null,
    dueTime: null,
    durationMinutes: null,
    goalType: kind === 'quit' ? 'numeric' : 'binary',
    target: kind === 'quit' ? 0 : 1,
    unit: null,
    schedule: { type: 'daily' },
    average: null,
    defaultIncrement: 1,
    primaryGroupId: null,
    secondaryGroupIds: [],
    hideStreak: false,
    excludedFromProgress: false,
  };
}

export function draftFromHabit(h: HabitRec): HabitDraft {
  const v = [...h.versions].sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : 1)).pop()!;
  return {
    kind: h.kind,
    name: h.name,
    description: h.description,
    icon: h.icon,
    color: h.color,
    startDate: h.startDate,
    endDate: h.endDate,
    dueDate: h.dueDate,
    dueTime: h.dueTime ?? null,
    durationMinutes: h.durationMinutes ?? null,
    goalType: v?.goalType ?? 'binary',
    target: v?.target ?? 1,
    unit: v?.unit ?? null,
    schedule: (v?.schedule as Schedule) ?? { type: 'daily' },
    average: v?.average ?? null,
    defaultIncrement: h.defaultIncrement,
    primaryGroupId: h.primaryGroupId,
    secondaryGroupIds: h.secondaryGroupIds,
    hideStreak: h.hideStreak,
    excludedFromProgress: h.excludedFromProgress,
  };
}

function versionFromDraft(d: HabitDraft, effectiveFrom: LocalDate): HabitVersion {
  return {
    effectiveFrom,
    goalType: d.kind === 'quit' ? 'numeric' : d.goalType,
    target: d.goalType === 'binary' && d.kind === 'build' ? 1 : d.target,
    unit: d.goalType === 'binary' && d.kind === 'build' ? null : d.unit,
    schedule: d.schedule,
    // Solo se escribe si aplica, para no cambiar la forma de las versiones existentes.
    ...(averageAllowed(d) && d.average ? { average: d.average } : {}),
  };
}

/** La media móvil solo tiene sentido en build numérico con periodos diarios. */
export function averageAllowed(d: Pick<HabitDraft, 'kind' | 'goalType' | 'schedule'>): boolean {
  return d.kind === 'build' && d.goalType === 'numeric' && d.schedule.type !== 'weekly_quota' && d.schedule.type !== 'monthly_quota';
}

/** Campos de un hábito o tarea nuevos. */
export function newHabitRecord(d: HabitDraft, sortKey: string, nowIso: string): Record<string, unknown> {
  return {
    kind: d.kind,
    name: d.name.trim(),
    description: d.description?.trim() || null,
    icon: d.icon,
    color: d.color,
    startDate: d.startDate,
    endDate: d.kind === 'task' ? null : d.endDate,
    dueDate: d.kind === 'task' ? d.dueDate : null,
    dueTime: d.kind === 'task' && d.dueDate ? d.dueTime : null,
    durationMinutes: d.kind === 'quit' ? null : d.durationMinutes,
    completedAt: null,
    archivedAt: null,
    sortKey,
    primaryGroupId: d.primaryGroupId,
    secondaryGroupIds: d.secondaryGroupIds.filter((g) => g !== d.primaryGroupId),
    hideStreak: d.hideStreak,
    excludedFromProgress: d.excludedFromProgress,
    defaultIncrement: d.defaultIncrement > 0 ? d.defaultIncrement : 1,
    versions: d.kind === 'task' ? [] : [versionFromDraft(d, d.startDate)],
    createdAt: nowIso,
    deletedAt: null,
  };
}

function sameConfig(a: HabitVersion, b: HabitVersion): boolean {
  return (
    a.goalType === b.goalType &&
    a.target === b.target &&
    (a.unit ?? null) === (b.unit ?? null) &&
    (a.average ?? null) === (b.average ?? null) &&
    JSON.stringify(a.schedule) === JSON.stringify(b.schedule)
  );
}

/** Calcula las versiones resultantes de una edición (FR-SCH-09). */
export function nextVersions(h: HabitRec, d: HabitDraft, today: LocalDate, applyToHistory: boolean): HabitVersion[] {
  if (h.kind === 'task') return [];
  const sorted = [...h.versions].sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : 1)) as HabitVersion[];
  const startDate = d.startDate;
  if (applyToHistory || sorted.length === 0) return [versionFromDraft(d, startDate)];
  const current = sorted[sorted.length - 1]!;
  const candidate = versionFromDraft(d, today < startDate ? startDate : today);
  if (sameConfig(current, candidate)) {
    // Solo puede cambiar la fecha de inicio: se ajusta la primera versión si quedó posterior.
    if (sorted[0]!.effectiveFrom > startDate) sorted[0] = { ...sorted[0]!, effectiveFrom: startDate };
    return sorted;
  }
  const kept = sorted.filter((v) => v.effectiveFrom < candidate.effectiveFrom);
  if (kept.length === 0) return [{ ...candidate, effectiveFrom: startDate }];
  return [...kept, candidate];
}

/** Campos que cambian al editar un hábito con el formulario (o desde el asistente). */
export function habitEditPatch(h: HabitRec, d: HabitDraft, today: LocalDate, applyToHistory: boolean): Record<string, unknown> {
  return {
    name: d.name.trim(),
    description: d.description?.trim() || null,
    icon: d.icon,
    color: d.color,
    startDate: d.startDate,
    endDate: h.kind === 'task' ? null : d.endDate,
    dueDate: h.kind === 'task' ? d.dueDate : null,
    dueTime: h.kind === 'task' && d.dueDate ? d.dueTime : null,
    durationMinutes: h.kind === 'quit' ? null : d.durationMinutes,
    primaryGroupId: d.primaryGroupId,
    secondaryGroupIds: d.secondaryGroupIds.filter((g) => g !== d.primaryGroupId),
    hideStreak: d.hideStreak,
    excludedFromProgress: d.excludedFromProgress,
    defaultIncrement: d.defaultIncrement > 0 ? d.defaultIncrement : 1,
    versions: nextVersions(h, d, today, applyToHistory),
  };
}

export function assertLoggable(h: HabitRec, date: LocalDate, ctx: Ctx): void {
  if (date > ctx.today) throw new DomainError('future_date');
  if (date < h.startDate) throw new DomainError('before_start');
}

/** Registrar en un día omitido retira la omisión (FR-LOG-06). */
export function skipRemoval(h: HabitRec, date: LocalDate, ctx: Ctx, overrides: OverrideRec[], nowIso: string): RecordWrite[] {
  const key = ueKeyForDate(toHabitDef(h), toEngineSettings(ctx.settings), date);
  if (!key) return [];
  const o = overrides.find((x) => x.habitId === h.id && x.ueKey === key && !x.deletedAt);
  return o && o.kind === 'skipped' ? [{ entity: 'overrides', id: o.id, patch: { deletedAt: nowIso } }] : [];
}

export function dayTotal(entries: EntryRec[], habitId: string, date: LocalDate): number {
  let t = 0;
  for (const e of entries) if (e.habitId === habitId && e.logicalDate === date && !e.deletedAt) t += e.value;
  return Math.max(0, Math.round(t * 1000) / 1000);
}

/**
 * Escrituras para registrar una cantidad (positiva o negativa) en un día lógico. El total del
 * día no puede quedar por debajo de 0 (FR-LOG-02). Vacío si no hay nada que registrar.
 */
export function logWrites(
  h: HabitRec,
  date: LocalDate,
  delta: number,
  ctx: Ctx,
  entries: EntryRec[],
  overrides: OverrideRec[],
  env: { newId: () => string; nowIso: string; source?: 'manual' | 'timer' | 'import' | 'health' },
  note: string | null = null,
): RecordWrite[] {
  assertLoggable(h, date, ctx);
  const total = dayTotal(entries, h.id, date);
  const value = delta < 0 ? -Math.min(total, -delta) : delta;
  if (value === 0) return [];
  return [
    {
      entity: 'entries',
      id: env.newId(),
      patch: { habitId: h.id, logicalDate: date, value, occurredAt: env.nowIso, tz: ctx.tz, source: env.source ?? 'manual', note, createdAt: env.nowIso, deletedAt: null },
    },
    ...(value > 0 ? skipRemoval(h, date, ctx, overrides, env.nowIso) : []),
  ];
}
